import React, { useState, useRef, useEffect } from "react";
import {
    Drawer, Card, Typography, Tag, Alert, Input, message,
} from "antd";
import {
    KeyOutlined, SafetyOutlined, IdcardOutlined, BarcodeOutlined,
    CheckCircleFilled, CloseCircleFilled, PictureOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faPlus, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { SET_KEY_AUDIT_REQUEST_EXPIRE_Mutation, submitKeyAuditResultMutation } from "../../util/mutation/keyRegister.mutation";
import { genericFileUploadSignatureMutation } from "@src/util/mutation/genericFileUpload.mutation";
import axios from "axios";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { GET_KEY_AUDIT_REQUEST_Query, getKeyByIdQuery } from "../../util/query/keyRegister.query";

const { Text, Title } = Typography;
const { TextArea } = Input;

const KEY_TYPE_CONFIG = {
    metal:      { label: "Metal",       icon: <KeyOutlined />,      color: "#8c8c8c" },
    fob:        { label: "Fob",         icon: <SafetyOutlined />,   color: "#1677ff" },
    card:       { label: "Card",        icon: <IdcardOutlined />,   color: "#722ed1" },
    accessCode: { label: "Access Code", icon: <BarcodeOutlined />,  color: "#13c2c2" },
};

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_MB = 5;
const THEME_COLOR = "rgb(13, 92, 99)";

// Inline validation text — shown on the form itself instead of a toast.
const FieldError = ({ children }) => !children ? null : (
    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 6 }}>{children}</div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const ConfirmBtn = ({ active, color, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            padding: "6px 20px",
            borderRadius: 6,
            border: active ? `1.5px solid ${color}` : "1px solid #d9d9d9",
            background: active ? color : "#fff",
            color: active ? "#fff" : "#595959",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: 13,
            transition: "all 0.2s",
            lineHeight: 1.4,
        }}
    >
        {children}
    </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export const KeyAuditConductModal = ({ visible, close, auditFor = "key", onComplete }) => {

    const param = useParams();


    const {data:auditRequestData, loading:auditRequestLoading} = useQuery(GET_KEY_AUDIT_REQUEST_Query,{
        variables:{
            auditRequestId: param?.auditId
        },
        skip: !param?.auditId,
        fetchPolicy: 'network-only'
    }); 

    const [setAuditRequestToExpire, {loading: setAuditRequestToExpireLoading}] = useMutation(SET_KEY_AUDIT_REQUEST_EXPIRE_Mutation)

    useEffect(()=>{
        if(!auditRequestData?.getKeyAuditRequest?.response?.isExpired){
            setAuditRequestToExpire({
                variables:{
                    auditRequestId: param?.auditId
                }
            });
        }
    },[auditRequestData?.getKeyAuditRequest?.response?.isExpired]);

    const [currentStep, setCurrentStep] = useState(0);
    const [confirmations, setConfirmations] = useState({});   // { metal: true, fob: false, … }
    const [reasons, setReasons]             = useState({});   // { fob: "reason text" }
    const [overallNotes, setOverallNotes]   = useState("");
    const [images, setImages]               = useState([]);
    const [submitted, setSubmitted]         = useState(false);
    const [stepError, setStepError]         = useState("");
    // submitLoading/uploadSigLoading only cover their own GraphQL calls, not the
    // axios.put S3 uploads that happen between them — track the whole submit
    // explicitly so the button stays disabled for the entire upload.
    const [isUploading, setIsUploading]     = useState(false);
    const fileInputRef = useRef(null);


    const {data, loading} = useQuery(getKeyByIdQuery,{
        variables:{
            keyId: param?.keyId
        },
        skip: !param?.keyId,
        fetchPolicy:'network-only'
    })

    const [keyData, setKeyData] = useState(null)
    
    useEffect(()=>{
        setKeyData(data?.getKeyById?.response);
    },[data?.getKeyById?.response]);



    const [submitAuditResult, { loading: submitLoading }]          = useMutation(submitKeyAuditResultMutation);
    const [genericFileUploadSignature, { loading: uploadSigLoading }] = useMutation(genericFileUploadSignatureMutation);

    // ── Derived data ──────────────────────────────────────────────────────────
    const keyTypes   = keyData?.keyTypes   || [];
    const siteName   = keyData?.site?.sitename       || "—";
    const keySerial  = keyData?.keySerial            || "—";
    const custodian  = keyData?.custodianDetail?.name || "—";
    const siteId     = keyData?.siteId               || "";

    const missingTypes     = keyTypes.filter(kt => confirmations[kt.type] === false);
    const hasAnyNo         = missingTypes.length > 0;
    const isStep1Complete  = keyTypes.length > 0 && keyTypes.every(kt => confirmations[kt.type] !== undefined);
    const isStep2Complete  = missingTypes.every(kt => reasons[kt.type]?.trim());

    // Steps are dynamic: skip "Reasons" step if all confirmed
    const steps = [
        { title: "Confirm Keys" },
        ...(hasAnyNo ? [{ title: "Reasons" }] : []),
        { title: "Photos & Submit" },
    ];
    const isLastStep = currentStep === steps.length - 1;

    // ── Reset on open ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (visible) {
            setCurrentStep(0);
            setConfirmations({});
            setReasons({});
            setOverallNotes("");
            setImages([]);
            setSubmitted(false);
            setStepError("");
        }
    }, [visible]);

    // Clear the inline error whenever the step changes, and as soon as what it's
    // complaining about is fixed.
    useEffect(() => { setStepError(""); }, [currentStep]);
    useEffect(() => { if (isStep1Complete) setStepError(""); }, [isStep1Complete]);
    useEffect(() => { if (isStep2Complete) setStepError(""); }, [isStep2Complete]);
    useEffect(() => { if (images.length) setStepError(""); }, [images.length]);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => images.forEach(img => img.preview && URL.revokeObjectURL(img.preview));
    }, []);

    // ── Image helpers ─────────────────────────────────────────────────────────
    const validateFile = (file) => {
        if (!file.type.startsWith("image/")) {
            message.error(`"${file.name}" is not an image file`);
            return false;
        }
        if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
            message.error(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB limit`);
            return false;
        }
        return true;
    };

    const addImageFiles = (files) => {
        const remaining = MAX_IMAGES - images.length;
        if (remaining <= 0) { message.warning(`Maximum ${MAX_IMAGES} images allowed`); return; }
        const valid = Array.from(files).slice(0, remaining).filter(validateFile);
        setImages(prev => [
            ...prev,
            ...valid.map(file => ({
                uid:     `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                file,
                name:    file.name,
                preview: URL.createObjectURL(file),
            })),
        ]);
    };

    const removeImage = (uid) => {
        setImages(prev => {
            const img = prev.find(i => i.uid === uid);
            if (img?.preview) URL.revokeObjectURL(img.preview);
            return prev.filter(i => i.uid !== uid);
        });
    };

    // ── Navigation ────────────────────────────────────────────────────────────
    const handleNext = () => {
        const label = steps[currentStep]?.title;
        if (label === "Confirm Keys" && !isStep1Complete) {
            setStepError("Please confirm all key types before proceeding");
            return;
        }
        if (label === "Reasons" && !isStep2Complete) {
            setStepError("Please provide a reason for each unconfirmed key type");
            return;
        }
        setCurrentStep(s => s + 1);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (images.length === 0) {
            setStepError("Please upload at least one photo as audit evidence");
            return;
        }
        setIsUploading(true);
        try {
            // Upload images to S3
            const imagePayloads = [];
            await Promise.all(images.map(async (img) => {
                const imgName = `keys/${siteId}/${keySerial}-${dayjs().valueOf()}-${img.name.replaceAll(" ", "-")}`.toLowerCase();
                const urlRes = await genericFileUploadSignature({
                    variables: { input: { fileName: imgName, objectType: "keys" } },
                });
                const uploadResponse = urlRes?.data?.genericFileUploadSignatureUrl?.response;
                await axios.put(uploadResponse?.url, img.file, { headers: { "Content-Type": img.file.type } });
                // The server strips whitespace from the filename before uploading — store the
                // name it actually used, or the photo won't be found again by the exact key.
                imagePayloads.push(uploadResponse?.originalFileName || imgName);
            }));

            const keyConfirmations = keyTypes.map(kt => ({
                keyType:       kt.type,
                expectedCount: kt.count,
                confirmed:     confirmations[kt.type] === true,
                reason:        confirmations[kt.type] === false ? (reasons[kt.type] || "") : null,
            }));

            await submitAuditResult({
                variables: {
                    input: {
                        auditFor,
                        keyId:           auditFor === "key"      ? keyData?._id : null,
                        keyGroupId:      auditFor === "keygroup"  ? keyData?._id : null,
                        keyConfirmations,
                        isFullyConfirmed: !hasAnyNo,
                        overallNotes,
                        images:          imagePayloads,
                        auditRequestId: param?.auditId
                    },
                },
            });

            if (onComplete) onComplete();
            setSubmitted(true);
        } catch (err) {
            message.error(err?.message || "Failed to submit audit");
        } finally {
            setIsUploading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Step renders
    // ─────────────────────────────────────────────────────────────────────────

    // Step 0 — Confirm Keys
    const renderStep0 = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Key identity card */}
            <Card
                style={{ borderRadius: 10, background: "#f6f8ff", border: "1px solid #dce3ff" }}
                bodyStyle={{ padding: "16px 20px" }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                        { label: "Site",      value: siteName },
                        { label: "Key Serial",value: <Text code style={{ fontSize: 13 }}>{keySerial}</Text> },
                        { label: "Custodian", value: custodian },
                    ].map(row => (
                        <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{row.label}</Text>
                            <Text strong style={{ fontSize: 13 }}>{row.value}</Text>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Instruction */}
            <Text type="secondary" style={{ fontSize: 13 }}>
                Confirm whether you currently have each of the following key types in your possession:
            </Text>

            {/* Per key-type confirmation rows */}
            {keyTypes.map(kt => {
                const cfg       = KEY_TYPE_CONFIG[kt.type] || {};
                const confirmed = confirmations[kt.type];

                return (
                    <Card
                        key={kt.type}
                        style={{
                            borderRadius: 10,
                            border: confirmed === true
                                ? "1.5px solid #52c41a"
                                : confirmed === false
                                ? "1.5px solid #ff4d4f"
                                : "1px solid #f0f0f0",
                            background: confirmed === true
                                ? "#f6ffed"
                                : confirmed === false
                                ? "#fff2f0"
                                : "#fff",
                            transition: "all 0.25s",
                        }}
                        bodyStyle={{ padding: "14px 18px" }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>

                            {/* Type info */}
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 8,
                                    background: `${cfg.color}18`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: cfg.color, fontSize: 17,
                                }}>
                                    {cfg.icon}
                                </div>
                                <div>
                                    <Text strong style={{ fontSize: 14, display: "block" }}>{cfg.label} Keys</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Expected quantity: <strong>{kt.count}</strong>
                                    </Text>
                                </div>
                            </div>

                            {/* Confirm / Deny */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {confirmed !== undefined && (
                                    confirmed
                                        ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />
                                        : <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />
                                )}
                                <ConfirmBtn
                                    active={confirmed === true}
                                    color="#52c41a"
                                    onClick={() => setConfirmations(prev => ({ ...prev, [kt.type]: true }))}
                                >
                                    ✓ Yes
                                </ConfirmBtn>
                                <ConfirmBtn
                                    active={confirmed === false}
                                    color="#ff4d4f"
                                    onClick={() => setConfirmations(prev => ({ ...prev, [kt.type]: false }))}
                                >
                                    ✕ No
                                </ConfirmBtn>
                            </div>
                        </div>

                        {/* Access codes preview */}
                        {kt.type === "accessCode" && kt.accessCodes?.length > 0 && (
                            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {kt.accessCodes.map((ac, i) => (
                                    <Tag key={i} style={{ borderRadius: 4, fontFamily: "monospace" }}>
                                        {ac.code}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                );
            })}

            {/* All-confirmed banner */}
            {isStep1Complete && !hasAnyNo && (
                <Alert
                    type="success"
                    showIcon
                    message="All key types confirmed!"
                    description="You can proceed to upload audit photos."
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* Some-missing banner */}
            {isStep1Complete && hasAnyNo && (
                <Alert
                    type="warning"
                    showIcon
                    message={`${missingTypes.length} key type${missingTypes.length > 1 ? "s" : ""} not confirmed`}
                    description="Please proceed to provide reasons for the unconfirmed keys."
                    style={{ borderRadius: 8 }}
                />
            )}

            <FieldError>{stepError}</FieldError>
        </div>
    );

    // Step 1 (conditional) — Reasons
    const renderStep1Reasons = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Alert
                type="warning"
                showIcon
                message="Missing Key Details"
                description="Please provide a reason for each key type that was not confirmed."
                style={{ borderRadius: 8 }}
            />

            {missingTypes.map(kt => {
                const cfg = KEY_TYPE_CONFIG[kt.type] || {};
                return (
                    <Card
                        key={kt.type}
                        style={{ borderRadius: 10, border: "1.5px solid #ffe58f", background: "#fffbe6" }}
                        bodyStyle={{ padding: "14px 18px" }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                            <span style={{ color: cfg.color, fontSize: 16 }}>{cfg.icon}</span>
                            <Text strong style={{ fontSize: 14 }}>{cfg.label} Keys</Text>
                            <Tag color="error" style={{ marginLeft: 4 }}>Not Confirmed</Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                (expected {kt.count})
                            </Text>
                        </div>
                        <TextArea
                            rows={3}
                            placeholder={`Reason for missing ${cfg.label} keys…`}
                            className="generic-input-control"
                            value={reasons[kt.type] || ""}
                            onChange={e => setReasons(prev => ({ ...prev, [kt.type]: e.target.value }))}
                        />
                    </Card>
                );
            })}

            <div>
                <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
                    Additional Notes{" "}
                    <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span>
                </Text>
                <TextArea
                    rows={3}
                    placeholder="Any other context about this audit…"
                    className="generic-input-control"
                    value={overallNotes}
                    onChange={e => setOverallNotes(e.target.value)}
                />
            </div>

            <FieldError>{stepError}</FieldError>
        </div>
    );

    // Last step — Photos & Submit
    const renderStepPhotos = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Audit summary */}
            <Card
                style={{ borderRadius: 10, background: "#fafafa", border: "1px solid #f0f0f0" }}
                bodyStyle={{ padding: "14px 18px" }}
            >
                <Text strong style={{ fontSize: 13, display: "block", marginBottom: 10 }}>Audit Summary</Text>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {keyTypes.map(kt => {
                        const cfg       = KEY_TYPE_CONFIG[kt.type] || {};
                        const confirmed = confirmations[kt.type];
                        return (
                            <div key={kt.type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ color: cfg.color, fontSize: 14 }}>{cfg.icon}</span>
                                    <Text style={{ fontSize: 13 }}>
                                        {cfg.label} × <strong>{kt.count}</strong>
                                    </Text>
                                </div>
                                {confirmed
                                    ? <Tag color="success" style={{ borderRadius: 4 }}>Confirmed</Tag>
                                    : <Tag color="error"   style={{ borderRadius: 4 }}>Not Confirmed</Tag>}
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Photo upload area */}
            <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <PictureOutlined style={{ color: THEME_COLOR }} />
                    <Text strong style={{ fontSize: 13 }}>
                        Audit Photos{" "}
                        <span style={{ color: "#999", fontWeight: 400 }}>
                            ({images.length}/{MAX_IMAGES})
                        </span>
                    </Text>
                    <Text type="danger" style={{ fontSize: 12 }}>*</Text>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {images.map(img => (
                        <div
                            key={img.uid}
                            style={{
                                position: "relative", width: 88, height: 88,
                                borderRadius: 8, overflow: "hidden", border: "1px solid #e8e8e8",
                            }}
                        >
                            <img
                                src={img.preview}
                                alt={img.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(img.uid)}
                                style={{
                                    position: "absolute", top: 4, right: 4,
                                    background: "rgba(0,0,0,0.55)", border: "none",
                                    borderRadius: "50%", width: 20, height: 20,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", padding: 0,
                                }}
                            >
                                <FontAwesomeIcon icon={faClose} style={{ color: "#fff", fontSize: 10 }} />
                            </button>
                        </div>
                    ))}

                    {images.length < MAX_IMAGES && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                width: 88, height: 88, borderRadius: 8,
                                border: "2px dashed #d9d9d9",
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center",
                                cursor: "pointer", background: "#fafafa", gap: 4,
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = THEME_COLOR;
                                e.currentTarget.style.background  = "rgba(13, 92, 99, 0.06)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = "#d9d9d9";
                                e.currentTarget.style.background  = "#fafafa";
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 16, color: "#8c8c8c" }} />
                            <span style={{ fontSize: 11, color: "#8c8c8c" }}>Add Photo</span>
                        </div>
                    )}
                </div>

                {images.length === 0 && (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            marginTop: 10,
                            border: "2px dashed #d9d9d9",
                            borderRadius: 8,
                            padding: "28px 20px",
                            textAlign: "center",
                            cursor: "pointer",
                            background: "#fafafa",
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.borderColor = THEME_COLOR;
                            e.currentTarget.style.background  = "rgba(13, 92, 99, 0.06)";
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.borderColor = "#d9d9d9";
                            e.currentTarget.style.background  = "#fafafa";
                        }}
                    >
                        <FontAwesomeIcon icon={faUpload} style={{ fontSize: 22, color: "#8c8c8c", marginBottom: 8 }} />
                        <p style={{ margin: 0, fontSize: 14, color: "#595959", fontWeight: 500 }}>
                            Click to upload audit photos
                        </p>
                        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#999" }}>
                            Up to {MAX_IMAGES} images, max {MAX_FILE_SIZE_MB} MB each
                        </p>
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={e => { if (e.target.files?.length) addImageFiles(e.target.files); e.target.value = ""; }}
                />
            </div>

            {/* Notes (only here if all keys confirmed; otherwise captured in reasons step) */}
            {!hasAnyNo && (
                <div>
                    <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
                        Notes{" "}
                        <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span>
                    </Text>
                    <TextArea
                        rows={3}
                        placeholder="Any additional notes about this audit…"
                        className="generic-input-control"
                        value={overallNotes}
                        onChange={e => setOverallNotes(e.target.value)}
                    />
                </div>
            )}

            <FieldError>{stepError}</FieldError>
        </div>
    );

    // ── Success screen ────────────────────────────────────────────────────────
    const renderSuccess = () => (
        <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 20,  textAlign: "center", height:'60vh'
        }}>
            <CheckCircleFilled style={{ fontSize: 72, color: "#52c41a" }} />
            <div>
                <Title level={3} style={{ margin: 0, color: "#262626" }}>Audit Submitted!</Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                    Thank you <br/> The key audit has been recorded successfully.
                </Text>
            </div>
            {/* <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button className="drawer-outlined-btn" onClick={()=>window.close()}>
                    Close
                </button>
            </div> */}
        </div>
    );

    // ── Step content map ──────────────────────────────────────────────────────
    const stepContents = [
        renderStep0(),
        ...(hasAnyNo ? [renderStep1Reasons()] : []),
        renderStepPhotos(),
    ];

    const isSubmitting = submitLoading || uploadSigLoading || isUploading;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Drawer
            title="Conduct Key Audit"
            placement="right"
            closable={true}
            onClose={close}
            // closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon"  style={{marginRight:'120px'}}/>}
            visible={true}
            // style={{width:'100%'}}
            width={"100%"}
            maskClosable={false}
            mask={true}
            loading={auditRequestLoading || loading || setAuditRequestToExpireLoading}
            footer={
                !submitted && !auditRequestData?.getKeyAuditRequest?.response?.isExpired && (
                    <div
                        className="drawer-footer"
                        style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
                    >
                        {currentStep > 0 && (
                            <button
                                className="drawer-outlined-btn"
                                onClick={() => setCurrentStep(s => s - 1)}
                                disabled={isSubmitting}
                            >
                                Previous
                            </button>
                        )}

                        {!isLastStep ? (
                            <button className="drawer-filled-btn" onClick={handleNext}>
                                Next
                            </button>
                        ) : (
                            <button
                                className="drawer-filled-btn"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Submitting…" : "Submit Audit"}
                            </button>
                        )}
                    </div>
                )
            }
        >
            {auditRequestData?.getKeyAuditRequest?.response?.isExpired ? (
                <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", gap: 20, textAlign: "center", height: "60vh",
                }}>
                    <CloseCircleFilled style={{ fontSize: 100, color: "#ff4d4f" }} />
                    <div>
                        <Title level={1} style={{ margin: 0, color: "#262626" }}>Link Already Expired</Title>
                        <br/>
                        <Text type="secondary" style={{ fontSize: 14 }}>
                            Thanks for using WFC360
                        </Text>
                    </div>
                </div>
            ) : submitted ? renderSuccess() : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Text strong style={{ fontSize: 16 }}>{steps[currentStep]?.title}</Text>

                    {/* Active step content */}
                    <div>{stepContents[currentStep]}</div>
                </div>
            )}
        </Drawer>
    );
};
